import { useEffect, useState } from 'react';
import { exportOutcomesCSV } from '../../../api/portfolio-client/portfolio-client';
import { useOutcomes } from '../../../api/strategy-client/strategy-client.hooks';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import {
  setSelectedOutcome,
  setTableSortBy,
  setTableSortDirection,
} from '../../../store/strategy-store/strategy-store.actions';
import { OutcomesMainSectionView } from './outcomes-main-section-view';
import { OutcomePresentationModal } from './presentation-modal/presentation-modal';

type Props = {
  isLoading: boolean;
};

// Module-level so the selector's fallback keeps a stable identity. A fresh `[]` per
// evaluation changes the page-reset effect's dependency on every render, which pins the
// table to page 1 and makes pagination impossible whenever the key is absent.
const NO_TEAM_FILTER: string[] = [];

export function OutcomesMainSection({ isLoading: _isLoading }: Props) {
  const { outcomes } = useOutcomes();
  const teams = useGlobalStore((state) => state.teams);
  const organization = useGlobalStore((state) => state.organization);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const selectedOutcome = useStrategyStore((state) => state.selectedOutcome);
  const tableSortBy = useStrategyStore((state) => state.tableSortBy);
  const tableSortDirection = useStrategyStore((state) => state.tableSortDirection);
  const statusFilters = useStrategyStore((state) => state.filters.status);
  const onlyBookmarkedFilter = useStrategyStore((state) => state.filters.only_bookmarked);
  const includeEmptyFilter = useStrategyStore((state) => state.filters.include_empty);
  // Falls back to a stable constant if a persisted blob ever reaches this read without the
  // key (e.g. a missed persist version bump or a rollback).
  const outcomeTeamsFilter = useStrategyStore((state) => state.filters.outcome_teams) ?? NO_TEAM_FILTER;
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [presentationModalOpen, setPresentationModalOpen] = useState(false);

  // Filter outcomes based on search, status, bookmark, teams, and include empty
  const filteredOutcomes = outcomes.filter((outcome) => {
    // Include empty filter (show only outcomes with initiatives when OFF)
    if (!includeEmptyFilter) {
      if (!outcome.initiatives || outcome.initiatives.length === 0) {
        return false;
      }
    }

    // Search filter
    const matchesSearch =
      outcome.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (outcome.key && outcome.key.toLowerCase().includes(searchTerm.toLowerCase()));

    // Status filter
    const outcomeStatus = outcome.status?.by_tasks?.status || 'unknown';
    const matchesStatus = statusFilters.length === 0 || statusFilters.includes(outcomeStatus);

    // Bookmark filter
    const matchesBookmark = !onlyBookmarkedFilter || outcome.is_bookmarked;

    // Team filter — empty selection means "all teams". `teams` is guarded because the
    // field is absent until the backend ships it.
    const matchesTeams =
      outcomeTeamsFilter.length === 0 || (outcome.teams ?? []).some((team) => outcomeTeamsFilter.includes(team.id));

    return matchesSearch && matchesStatus && matchesBookmark && matchesTeams;
  });

  // Narrowing the result set can strand the user on a page that no longer exists, which
  // renders an empty table behind a nonsensical "Showing 81 to 4 of 4" summary. The filter
  // is the trigger here, not a value the effect reads, so biome's suggested fix (dropping
  // the dep) would reset the page on every render and break pagination outright.
  // biome-ignore lint/correctness/useExhaustiveDependencies(outcomeTeamsFilter): reset must run only when the selection changes
  useEffect(() => {
    setCurrentPage(1);
  }, [outcomeTeamsFilter]);

  const handleSortChange = (sortBy: string, direction: 'asc' | 'desc') => {
    setTableSortBy(sortBy as any);
    setTableSortDirection(direction as any);
  };

  const handleExportCSV = async () => {
    if (!organization?.id) return;
    try {
      const outcomeIds = filteredOutcomes.map((outcome) => outcome.id);
      await exportOutcomesCSV(organization.id, portfolioId, outcomeIds);
    } catch (error) {
      console.error('Failed to export outcomes CSV:', error);
    }
  };

  return (
    <>
      <OutcomesMainSectionView
        filteredOutcomes={filteredOutcomes}
        teams={teams}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedOutcomeId={selectedOutcome?.id}
        onOutcomeClick={setSelectedOutcome}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        tableSortBy={tableSortBy}
        tableSortDirection={tableSortDirection}
        onSortChange={handleSortChange}
        onExportCSV={handleExportCSV}
        onCreatePresentation={() => setPresentationModalOpen(true)}
        canExportPresentations={!!organization?.can_export_presentations}
      />
      {organization?.can_export_presentations && (
        <OutcomePresentationModal
          opened={presentationModalOpen}
          onClose={() => setPresentationModalOpen(false)}
          outcomeIds={filteredOutcomes.map((outcome) => outcome.id)}
          outcomesCount={filteredOutcomes.length}
        />
      )}
    </>
  );
}
